import { runGuarded } from './runGuarded';
import type { WAFClearanceDetail, WAFTerminalFailureOutcome } from './types';

export interface WAFTerminalFailure {
  outcome: WAFTerminalFailureOutcome;
  detail?: WAFClearanceDetail;
}

let currentFailure: WAFTerminalFailure | null = null;
let mountedFailureSurfaceCount = 0;
const subscribers = new Set<() => void>();

function notify(): void {
  subscribers.forEach((subscriber) => runGuarded(subscriber));
}

/**
 * Publish a terminal WAF failure to the mounted modal. The shared recoverWAFChallenge loop is the sole
 * publisher, so this covers terminal failures from both the axios and ai-chat transports. The first failure
 * wins and holds until a recovery clears it: a later publish while one is already present (identical or
 * different) is a no-op, so the blocking modal never thrashes its message under the user.
 */
export function publishWAFTerminalFailure(outcome: WAFTerminalFailureOutcome, detail?: WAFClearanceDetail): void {
  if (currentFailure !== null) {
    return;
  }
  currentFailure = { outcome, detail };
  notify();
}

/**
 * A recovery superseded a pending failure, but only the specific one the caller saw before its replay. The
 * recovery loop captures the snapshot before its attempt and clears it after the challenge clears, so a failure
 * a CONCURRENT recovery loop published during that attempt (a different reference) is never erased.
 */
export function clearWAFTerminalFailureIfUnchanged(expected: WAFTerminalFailure | null): void {
  if (expected === null || currentFailure !== expected) {
    return;
  }
  currentFailure = null;
  notify();
}

export function subscribeWAFFailure(callback: () => void): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/** Called by the mounted failure modal; the returned cleanup runs when it unmounts, including on a crash. */
export function registerWAFFailureSurface(): () => void {
  mountedFailureSurfaceCount += 1;
  return () => {
    mountedFailureSurfaceCount -= 1;
  };
}

export function isWAFFailureBlockingApp(): boolean {
  return mountedFailureSurfaceCount > 0 && currentFailure !== null;
}

/** Returns a STABLE reference while unchanged, so useSyncExternalStore's Object.is check never loops. */
export function getWAFFailureSnapshot(): WAFTerminalFailure | null {
  return currentFailure;
}

export function TestOnlyResetWAFFailureStore(): void {
  currentFailure = null;
  mountedFailureSurfaceCount = 0;
  subscribers.clear();
}
